<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

import { apiRequest, assertOk } from '../lib/apiClient'
import { useAuthStore } from '../stores/auth'
import { usePaymentModalStore, type PaymentEntry, type PaymentSupplier } from '../stores/paymentModal'

type CreditsSku = {
  sku_id: string
  id?: string
  name?: string
  description?: string
  amount?: number
  price?: number
  original_price?: number
  promotion?: string
}
type CreditsSkuResponse = { items?: CreditsSku[] }
type PaymentQrCodeResponse = { code_url?: string; order_id?: string }
type PaymentStatusResponse = { status?: string }

const auth = useAuthStore()
const payment = usePaymentModalStore()

const rechargeBusy = ref(false)
const rechargeError = ref('')
const rechargeSkus = ref<CreditsSku[]>([])
const rechargeSelectedSkuId = ref('')
const rechargeSupplier = ref<PaymentSupplier>('wechat')
const rechargeQrPayload = ref('')
const rechargeQrDataUrl = ref('')
const rechargeOrderId = ref('')
const rechargePayState = ref<'idle' | 'loading' | 'ready' | 'success' | 'error'>('idle')
const rechargePollTimer = ref<number>(0)
const rechargeTimeoutTimer = ref<number>(0)
const paymentFrom = 'wx18348bdcceb7cab7'
const skuCellRefs = ref<HTMLButtonElement[]>([])
const skuCellHeight = ref(0)
const skuCellExtraHeight = 32
let skuHeightTimer = 0

const entry = computed<PaymentEntry>(() => (payment.request?.entry as any) || 'manual')
const creditsRequired = computed(() => Number(payment.request?.creditsRequired || 0))

const rechargeTipText = computed(() => {
  const custom = String(payment.request?.tipText || '').trim()
  if (custom) return custom
  if (entry.value === 'insufficient') {
    const n = Math.max(0, Math.round(creditsRequired.value || 0))
    return `本次任务需消耗${n}积分，您的当前积分余额不足，请充值后再次提交。`
  }
  return '请选择充值套餐，并使用微信或支付宝扫码支付。'
})

function withBase(p: string) {
  const s = String(p || '').trim()
  if (!s) return ''
  if (/^https?:\/\//.test(s)) return s
  const rel = s.replace(/^\//, '')
  const baseRaw = (import.meta.env.BASE_URL as string | undefined) || '/'
  const base = baseRaw === '/' ? '/' : baseRaw.replace(/\/?$/, '/')
  return `${base}${rel}`
}

function formatYuanFromFen(fen: any) {
  const n = Number(fen || 0)
  if (!Number.isFinite(n)) return '0'
  return (Math.round(n) / 100).toFixed(2)
}

function formatYuanFromFenCompact(fen: any) {
  const s = formatYuanFromFen(fen)
  if (s.endsWith('.00')) return s.slice(0, -3)
  return s
}

function formatUnitYuanPerCredit(priceFen: any, amount: any) {
  const p = Number(priceFen || 0)
  const a = Number(amount || 0)
  if (!Number.isFinite(p) || !Number.isFinite(a) || a <= 0) return ''
  return (p / 100 / a).toFixed(2)
}

let qrLib: any = null
async function getQrLib() {
  if (qrLib) return qrLib
  const mod: any = await import('qrcode-generator')
  qrLib = mod?.default || mod
  return qrLib
}

async function buildQrDataUrl(payload: string) {
  const text = String(payload || '').trim()
  if (!text) return ''
  const qrcode: any = await getQrLib()
  const qr = qrcode(0, 'M')
  qr.addData(text)
  qr.make()
  return qr.createDataURL(8, 0)
}

function stopRechargePoll() {
  if (rechargePollTimer.value) {
    window.clearInterval(rechargePollTimer.value)
    rechargePollTimer.value = 0
  }
}

function stopRechargeTimeout() {
  if (rechargeTimeoutTimer.value) {
    window.clearTimeout(rechargeTimeoutTimer.value)
    rechargeTimeoutTimer.value = 0
  }
}

function resetState() {
  stopRechargePoll()
  stopRechargeTimeout()
  rechargeBusy.value = false
  rechargeError.value = ''
  rechargeSkus.value = []
  rechargeSelectedSkuId.value = ''
  rechargeQrPayload.value = ''
  rechargeQrDataUrl.value = ''
  rechargeOrderId.value = ''
  rechargePayState.value = 'idle'
  const requestedSupplier = String((payment.request?.supplier as any) || '').trim().toLowerCase()
  rechargeSupplier.value = requestedSupplier === 'alipay' ? 'alipay' : 'wechat'
  skuCellRefs.value = []
  skuCellHeight.value = 0
  if (skuHeightTimer) {
    window.clearTimeout(skuHeightTimer)
    skuHeightTimer = 0
  }
}

async function syncSkuCellHeight() {
  if (!payment.open) return
  skuCellHeight.value = 0
  await nextTick()
  const list = skuCellRefs.value.filter(Boolean)
  let maxH = 0
  for (const el of list) {
    const h = Math.round(el.getBoundingClientRect().height || 0)
    if (h > maxH) maxH = h
  }
  skuCellHeight.value = maxH > 0 ? maxH + skuCellExtraHeight : 0
}

function scheduleSyncSkuCellHeight() {
  if (skuHeightTimer) window.clearTimeout(skuHeightTimer)
  skuHeightTimer = window.setTimeout(() => {
    skuHeightTimer = 0
    void syncSkuCellHeight()
  }, 80)
}

async function fetchCreditsSkus() {
  rechargeError.value = ''
  if (!auth.token) throw new Error('未登录')
  const resp = await apiRequest<CreditsSkuResponse>('/preset/creditsSKU', { method: 'GET', token: auth.token })
  const data = assertOk(resp, '充值列表获取失败')
  const items = Array.isArray((data as any).items) ? (data as any).items : []
  const normalized = items
    .map((x: any) => ({
      ...x,
      sku_id: String(x?.id || x?.sku_id || x?.SKU_id || x?.skuId || '').trim(),
      name: String(x?.name || '').trim(),
    }))
    .filter((x: any) => x.sku_id)
  rechargeSkus.value = normalized
  const next = normalized[1]?.sku_id || normalized[0]?.sku_id || ''
  rechargeSelectedSkuId.value = next
}

async function fetchPayQrCode() {
  const skuId = rechargeSelectedSkuId.value
  if (!skuId) return
  if (!auth.token) return
  rechargePayState.value = 'loading'
  rechargeQrPayload.value = ''
  rechargeQrDataUrl.value = ''
  rechargeOrderId.value = ''
  stopRechargePoll()
  stopRechargeTimeout()
  try {
    const resp = await apiRequest<PaymentQrCodeResponse>('/Payment/QRCode', {
      method: 'POST',
      token: auth.token,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        SKU_id: skuId,
        from: String(payment.request?.from || paymentFrom),
        supplier: rechargeSupplier.value,
      }),
    } as any)
    const data = assertOk(resp, '获取支付码失败')
    const payload = String((data as any).code_url || '').trim()
    if (!payload) throw new Error('获取支付码失败')
    const orderId = String((data as any).order_id || '').trim()
    if (!orderId) throw new Error('获取支付码失败')
    rechargeQrPayload.value = payload
    rechargeQrDataUrl.value = rechargeSupplier.value === 'wechat' ? await buildQrDataUrl(payload) : ''
    rechargeOrderId.value = orderId
    rechargePayState.value = 'ready'
  } catch {
    rechargePayState.value = 'error'
    rechargeError.value = '获取支付码失败，请重新获取二维码后重试'
  }
}

async function pollPayStatus() {
  if (!payment.open) return
  if (rechargePayState.value !== 'ready') return
  if (!rechargeOrderId.value) return
  if (!auth.token) return
  try {
    const resp = await apiRequest<PaymentStatusResponse>(`/Payment/checkCaptured?order_id=${encodeURIComponent(rechargeOrderId.value)}`, {
      method: 'GET',
      token: auth.token,
    })
    const data = assertOk(resp, '查询支付状态失败')
    const st = String((data as any).status || '').toLowerCase()
    if (st === 'finished') {
      rechargePayState.value = 'success'
      stopRechargePoll()
      stopRechargeTimeout()
      try {
        await auth.fetchMe()
      } catch {}
      try {
        await payment.request?.onSuccess?.()
      } catch {}
      payment.success(rechargeOrderId.value, rechargeSelectedSkuId.value)
      resetState()
    } else if (st === 'error') {
      rechargePayState.value = 'error'
      stopRechargePoll()
      stopRechargeTimeout()
    }
  } catch {}
}

function startTimers() {
  stopRechargePoll()
  stopRechargeTimeout()
  rechargePollTimer.value = window.setInterval(() => void pollPayStatus(), 3000)
  rechargeTimeoutTimer.value = window.setTimeout(() => {
    if (!payment.open) return
    if (rechargePayState.value !== 'ready') return
    rechargePayState.value = 'error'
    stopRechargePoll()
    rechargeError.value = '支付超时，请重新获取二维码后重试'
  }, 5 * 60 * 1000)
}

watch(
  () => payment.open,
  async (open) => {
    if (!open) {
      resetState()
      return
    }
    resetState()
    if (!auth.token) {
      rechargeError.value = '请先登录后再充值'
      return
    }
    rechargeBusy.value = true
    try {
      await fetchCreditsSkus()
    } catch {
      rechargeError.value = '充值列表获取失败，请联系客服（微信：suiyu-network）协助解决。'
      return
    } finally {
      rechargeBusy.value = false
    }
    scheduleSyncSkuCellHeight()
    await nextTick()
    void fetchPayQrCode()
  },
)

watch(
  () => rechargeSkus.value.map((x) => `${x.sku_id}:${String(x.description || x.name || '').trim()}`).join('|'),
  () => scheduleSyncSkuCellHeight(),
)

watch(
  () => payment.open,
  (open) => {
    if (typeof window === 'undefined') return
    if (open) window.addEventListener('resize', scheduleSyncSkuCellHeight)
    else window.removeEventListener('resize', scheduleSyncSkuCellHeight)
  },
  { immediate: true },
)

watch(
  () => rechargePayState.value,
  (st) => {
    if (!payment.open) return
    if (st === 'ready') {
      startTimers()
      return
    }
    stopRechargePoll()
    stopRechargeTimeout()
  },
)

function closeRecharge() {
  payment.close({ status: 'cancel' })
  resetState()
}
function selectRechargeSupplier(supplier: PaymentSupplier) {
  const next = supplier === 'alipay' ? 'alipay' : 'wechat'
  if (next === rechargeSupplier.value) return
  rechargeSupplier.value = next
  rechargeError.value = ''
  void fetchPayQrCode()
}


function selectRechargeSku(skuId: string) {
  const next = String(skuId || '').trim()
  if (!next) return
  if (next === rechargeSelectedSkuId.value) return
  rechargeSelectedSkuId.value = next
  rechargeError.value = ''
  void fetchPayQrCode()
}

function retryPay() {
  rechargeError.value = ''
  if (!rechargeSelectedSkuId.value) return
  void fetchPayQrCode()
}

onBeforeUnmount(() => resetState())
</script>

<template>
  <div v-if="payment.open" class="modalOverlay" role="dialog" aria-modal="true" aria-label="充值" style="display: flex" @click.self="closeRecharge">
    <div class="modal modalRecharge">
      <div class="modalHeader">
        <strong>充值</strong>
        <button class="iconBtn" type="button" aria-label="关闭" @click="closeRecharge">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M18 6 6 18"></path>
            <path d="M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      <div class="modalBody">
        <div class="rechargeTip">{{ rechargeTipText }}</div>

        <div v-if="rechargeError" class="miniCard">
          <h4>充值失败</h4>
          <p>{{ rechargeError }}</p>
        </div>

        <div v-if="rechargeBusy" class="miniCard">
          <h4>正在为您获取优惠充值套餐</h4>
        </div>

        <template v-else>
          <div class="rechargeGrid" aria-label="支付区">
            <div aria-label="充值套餐">
              <div class="skuRow">
                <div class="skuItem" v-for="sku in rechargeSkus" :key="sku.sku_id">
                  <button
                    class="skuCell"
                    type="button"
                    ref="skuCellRefs"
                    :data-active="rechargeSelectedSkuId === sku.sku_id"
                    :aria-pressed="rechargeSelectedSkuId === sku.sku_id"
                    :style="skuCellHeight > 0 ? { height: `${skuCellHeight}px` } : undefined"
                    @click="selectRechargeSku(sku.sku_id)"
                  >
                    <div v-if="sku.promotion" class="skuPromo">{{ sku.promotion }}</div>
                    <div class="skuAmount">{{ Math.max(0, Math.round(sku.amount || 0)) }} 积分</div>
                    <div class="skuPriceRow">
                      <span class="skuPrice">¥{{ formatYuanFromFenCompact(sku.price) }}</span>
                      <span v-if="sku.original_price" class="skuOriginal">¥{{ formatYuanFromFenCompact(sku.original_price) }}</span>
                    </div>
                    <div v-if="formatUnitYuanPerCredit(sku.price, sku.amount)" class="skuUnitPriceTag">
                      {{ formatUnitYuanPerCredit(sku.price, sku.amount) }}/积分
                    </div>
                    <div class="skuDesc">{{ sku.description || sku.name || sku.sku_id }}</div>
                  </button>
                  <div class="skuName">{{ sku.name || sku.sku_id }}</div>
                </div>
              </div>
            </div>

            <div class="rechargeDivider" aria-hidden="true"></div>

            <div aria-label="支付">
              <div class="payTabBar" aria-label="支付方式">
                <button
                  class="payTab"
                  type="button"
                  :aria-pressed="rechargeSupplier === 'wechat'"
                  @click="selectRechargeSupplier('wechat')"
                >
                  微信支付
                </button>
                <button
                  class="payTab"
                  type="button"
                  :aria-pressed="rechargeSupplier === 'alipay'"
                  @click="selectRechargeSupplier('alipay')"
                >
                  支付宝支付
                </button>
              </div>
              <div class="qrBox" aria-label="支付二维码">
                <img
                  v-if="rechargeSupplier === 'wechat' && rechargeQrDataUrl"
                  class="qrImg"
                  :src="rechargeQrDataUrl"
                  alt="支付二维码"
                  width="184"
                  height="184"
                />
                <iframe
                  v-else-if="rechargeSupplier === 'alipay' && rechargeQrPayload"
                  class="qrIframe"
                  :src="rechargeQrPayload"
                  title="支付宝支付"
                  loading="lazy"
                  referrerpolicy="no-referrer"
                  scrolling="no"
                />
                <div v-else class="miniCard">
                  <h4>二维码加载中</h4>
                  <div v-if="rechargePayState === 'error'" style="margin-top: 12px; display: flex; justify-content: center">
                    <button class="cta loginBtn" type="button" @click="retryPay">重新获取二维码</button>
                  </div>
                </div>
              </div>

              <div style="margin-top: 10px; text-align: center; font-size: 12px; color: rgba(255, 255, 255, 0.72)">
                可开具电子发票，请联系客服。
              </div>

              <div class="payLogo" aria-label="支付方式">
                <img
                  v-if="rechargeSupplier === 'wechat'"
                  class="payLogoImg"
                  :src="withBase('/assets/pay-wechat.png')"
                  alt="微信支付"
                  width="240"
                  height="120"
                  loading="lazy"
                />
                <img
                  v-else
                  class="payLogoImg"
                  :src="withBase('/assets/pay-alipay.png')"
                  alt="支付宝支付"
                  width="240"
                  height="120"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </template>

        <div class="rechargeAgreement">
          支付即表示同意
          <a href="/user-agreement/" target="_blank" rel="noopener noreferrer">用户协议</a>
          与
          <a href="/refund-policy/" target="_blank" rel="noopener noreferrer">退款协议</a>
        </div>
      </div>
    </div>
  </div>
</template>
